import { map } from "lodash/fp";

import * as api from "@app/common/api";
import { isResponseErrorWithStatus } from "@app/common/utils";
import { parseUTCDateTimeFromString } from "@app/utils/utils";

import type { Upload } from "./types";

interface ApiUpload {
  cancelReason: "insufficient_minutes" | "unknown_error" | null;
  startTime: string;
  createdOn: string;
  updatedOn: string | null;
  durationInMinutes: number | null;
  id: string;
  name: string;
  sessionId: string | null;
  status: "pending_report" | "uploaded" | "completed" | "cancelled";
}

const parseUploadStatus = (
  apiStatus: ApiUpload["status"]
): Upload["status"] => {
  switch (apiStatus) {
    case "completed":
    case "uploaded":
    case "pending_report":
      return "complete";
    case "cancelled":
      return "error";
    default:
      return "loading";
  }
};

const parseUploadFromResponse = (apiUpload: ApiUpload): Partial<Upload> => {
  return {
    id: apiUpload.id,
    name: apiUpload.name,
    status: parseUploadStatus(apiUpload.status),
    duration: apiUpload.durationInMinutes,
    message: apiUpload.cancelReason,
    sessionId: apiUpload.sessionId,
    startTime: apiUpload.startTime
      ? parseUTCDateTimeFromString(apiUpload.createdOn)
      : null,
  };
};

const parseResponsesToUploads = (
  originalUploads: Upload[],
  results: (PromiseSettledResult<ApiUpload> & { upload: Upload })[]
): Upload[] => {
  const uploadsById = new Map<string, Upload>();
  for (const upload of originalUploads) {
    uploadsById.set(upload.id, upload);
  }

  for (const result of results) {
    const targetUpload = uploadsById.get(result.upload.id);
    if (!targetUpload) {
      continue;
    }

    if (result.status === "rejected") {
      uploadsById.set(targetUpload.id, {
        ...targetUpload,
        status: "error",
        message: "unknown_error",
      });
    } else {
      const newUpload = parseUploadFromResponse(result.value);
      uploadsById.set(targetUpload.id, { ...targetUpload, ...newUpload });
    }
  }

  return Array.from(uploadsById.values());
};

export const generateReportsBatch = async ({
  uploads,
}: {
  uploads: Upload[];
}): Promise<Upload[]> => {
  const body = map(
    (upload) => ({
      id: upload.id,
      status: "pending_report",
      name: upload.name,
      startTime: upload.startTime?.toISO(),
    }),
    uploads
  );

  try {
    await api.patch("/uploads", body, { withCreds: true });
  } catch (e) {
    // for backwards compatibility with AB. Remove when no longer needed (after 2023-09-22)
    if (isResponseErrorWithStatus(e, 405)) {
      throw Error("not_implemented");
    }

    let errorReason: Upload["errorReason"] = "unknown_error";
    if (isResponseErrorWithStatus(e, 402)) {
      errorReason = "insufficient_credits";
    }

    return map(
      (upload) => ({ ...upload, status: "error", errorReason }),
      uploads
    );
  }
  return uploads;
};

export const generateReports = async ({
  uploads,
}: {
  uploads: Upload[];
}): Promise<Upload[]> => {
  try {
    return await generateReportsBatch({ uploads });
  } catch (e: any) {
    if (e.message !== "not_implemented") {
      throw e;
    }
  }

  // Backwards compat with production AB. Remove when no longer needed (after 2023-09-22)
  const requests = map(async (upload) => {
    try {
      return (await api.patch(
        `/uploads/${upload.id}`,
        {
          status: "pending_report",
          name: upload.name,
          startTime: upload.startTime?.toISO(),
        },
        { withCreds: true }
      )) as Promise<ApiUpload>;
    } catch (e) {
      return Promise.reject(e);
    }
  }, uploads);

  const responses = await Promise.allSettled(requests);
  const zippedResponses = uploads.map((upload, i) => ({
    upload,
    ...responses[i],
  }));
  return parseResponsesToUploads(uploads, zippedResponses);
};
